import { createContext, PropsWithChildren, useContext, useEffect } from 'react';

/** Props accepted by {@link AgentContext}. */
type AgentContextProps = PropsWithChildren<{
  /**
   * Concise description of the page's stable purpose, used by Witty to resolve
   * references to the page currently open when a message is sent.
   */
  description: string;
  environmentScoped?: boolean;
}>;

type AgentContextRegistration = {
  id: symbol;
  description: string;
  depth: number;
  environmentScoped: boolean;
};

const registrations: AgentContextRegistration[] = [];
const AgentContextDepth = createContext(0);

const registerAgentContext = (
  description: string,
  depth: number,
  environmentScoped: boolean,
): (() => void) => {
  const registration = { id: Symbol(), description, depth, environmentScoped };
  registrations.push(registration);

  return () => {
    const index = registrations.findIndex(({ id }) => id === registration.id);
    if (index >= 0) registrations.splice(index, 1);
  };
};

export const getCurrentPageContext = (
  environmentName?: string,
): string | undefined => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return undefined;
  }

  const activeRegistration = registrations.reduce<
    AgentContextRegistration | undefined
  >(
    (selected, registration) =>
      !selected || registration.depth >= selected.depth
        ? registration
        : selected,
    undefined,
  );
  const description = activeRegistration?.description.trim();
  const context = [
    document.title.trim() && `Page title: ${document.title.trim()}`,
    window.location.pathname && `Page path: ${window.location.pathname}`,
    activeRegistration?.environmentScoped &&
      environmentName?.trim() &&
      `Selected environment: ${environmentName.trim()}`,
    description && `Page description: ${description}`,
  ].filter((value): value is string => Boolean(value));

  return context.length > 0 ? context.join('\n').slice(0, 2000) : undefined;
};

/**
 * Registers semantic metadata for the rendered page without adding DOM output.
 *
 * Contexts can be nested; the most specific mounted context is used. The
 * registration is removed automatically when this component unmounts.
 *
 * @param props - Component children and the stable semantic page description.
 * @returns A provider that preserves the rendered child tree.
 */
export const AgentContext = ({
  description,
  environmentScoped = false,
  children,
}: AgentContextProps): JSX.Element => {
  const parentDepth = useContext(AgentContextDepth);
  const depth = parentDepth + 1;

  useEffect(
    () => registerAgentContext(description, depth, environmentScoped),
    [description, depth, environmentScoped],
  );

  return (
    <AgentContextDepth.Provider value={depth}>
      {children}
    </AgentContextDepth.Provider>
  );
};
